import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type StaffRole = 'admin' | 'staff';
type StaffStatus = 'pending' | 'active' | 'rejected';

type Staff = {
  id: number;
  email: string;
  name: string;
  department: string;
  position: string;
  team: string;
  permissions: string;
  role: StaffRole;
  status: StaffStatus;
  createdAt: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

const STATUS_LABEL: Record<StaffStatus, string> = { pending: '승인 대기', active: '활성', rejected: '거절됨' };

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

export default function StaffPermissionsPage() {
  const session = getAdminSession();
  const [items, setItems] = useState<Staff[]>([]);
  const [drafts, setDrafts] = useState<Record<number, { team: string; permissions: string; role: StaffRole; status: StaffStatus }>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);

  const fetchItems = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<Staff[]>>(`${MainURL}/admin-console/platform-staff`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
        setDrafts(
          Object.fromEntries(
            res.data.data.map((s) => [s.id, { team: s.team, permissions: s.permissions, role: s.role, status: s.status }]),
          ),
        );
      } else {
        setErrorMsg(res.data?.message || '관리자 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '관리자 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const updateDraft = (id: number, patch: Partial<{ team: string; permissions: string; role: StaffRole; status: StaffStatus }>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const handleSave = async (id: number) => {
    if (!session) return;
    const draft = drafts[id];
    if (!draft) return;
    setSavingId(id);
    try {
      const res = await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/platform-staff/${id}`, {
        requesterId: session.id,
        ...draft,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">관리자 권한 관리</h2>
          <p className="admin-panel-desc">내부 관리자(디자이너 / 개발자 / CS)의 소속(팀)과 권한, 계정 상태를 관리합니다.</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>이메일</th>
              <th>이름</th>
              <th>부서/직책</th>
              <th>팀</th>
              <th>권한(콤마 구분)</th>
              <th>역할</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={8}>등록된 관리자가 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => {
                const draft = drafts[item.id] || { team: '', permissions: '', role: item.role, status: item.status };
                return (
                  <tr key={item.id}>
                    <td>{item.email}</td>
                    <td>{item.name}</td>
                    <td>
                      {item.department || '-'} / {item.position || '-'}
                    </td>
                    <td>
                      <input
                        type="text"
                        value={draft.team}
                        placeholder="예: 디자인"
                        onChange={(e) => updateDraft(item.id, { team: e.target.value })}
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={draft.permissions}
                        placeholder="예: cs,notice"
                        onChange={(e) => updateDraft(item.id, { permissions: e.target.value })}
                        style={{ width: 160 }}
                      />
                    </td>
                    <td>
                      <select value={draft.role} onChange={(e) => updateDraft(item.id, { role: e.target.value as StaffRole })}>
                        <option value="staff">일반</option>
                        <option value="admin">최종관리자</option>
                      </select>
                    </td>
                    <td>
                      <select value={draft.status} onChange={(e) => updateDraft(item.id, { status: e.target.value as StaffStatus })}>
                        {Object.entries(STATUS_LABEL).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn edit-btn"
                        onClick={() => handleSave(item.id)}
                        disabled={savingId === item.id}
                      >
                        저장
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
